import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  EmptyState,
  PageHeader,
  Skeleton,
  StatusBadge,
  Surface,
} from '../components/ui/Primitives';
import {
  createAccountCleanupPreview,
  getAccountCleanupJob,
  getAccountCleanupOverview,
  protectAccountCleanupOrganization,
  retryAccountCleanupJob,
  startAccountCleanup,
  unprotectAccountCleanupOrganization,
} from '../services/accountCleanupApi';
import './AccountCleanup.css';


const TERMINAL_JOB_STATUSES = new Set(['completed', 'partial', 'failed', 'expired']);


const errorMessage = (error, fallback) => (
  error?.response?.data?.error?.message || error?.message || fallback
);


const titleCase = (value) => String(value || 'unknown')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, letter => letter.toUpperCase());


const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};


const subscriptionLabel = (organization) => {
  if (organization.subscriptionStatusAvailable === false) return 'Stripe status unavailable';
  if (organization.subscriptionStatus === 'not_configured') return 'No Stripe subscription';
  return `Stripe ${titleCase(organization.subscriptionStatus)}`;
};


const statusTone = (status) => {
  if (['completed', 'closed', 'deleted', 'disabled', 'already_closed', 'already_deleted', 'not_configured'].includes(status)) return 'success';
  if (['failed', 'partial'].includes(status)) return 'danger';
  if (['queued', 'processing'].includes(status)) return 'info';
  if (['skipped', 'protected', 'changed'].includes(status)) return 'warning';
  return 'neutral';
};


function ConfirmationDialog({ preview, onClose, onConfirm, busy }) {
  const [phrase, setPhrase] = useState('');
  const expected = preview.confirmationPhrase;
  const matches = phrase === expected;

  return (
    <div className="cleanup-modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !busy) onClose();
    }}>
      <section className="cleanup-confirm-modal" role="alertdialog" aria-modal="true" aria-labelledby="cleanup-confirm-title">
        <span className="say-eyebrow">Final confirmation</span>
        <h2 id="cleanup-confirm-title">Start this {preview.environment} cleanup?</h2>
        <p>
          Sayable will release the listed Twilio numbers, close the subaccounts, delete the Stripe customers, and permanently remove each tenant from the Sayable database. These actions cannot be undone.
        </p>
        <div className="cleanup-confirm-summary">
          <span><strong>{preview.totals?.organizations || 0}</strong> organizations</span>
          <span><strong>{preview.totals?.twilioAccounts || 0}</strong> Twilio accounts</span>
          <span><strong>{preview.totals?.stripeCustomers || 0}</strong> Stripe customers</span>
        </div>
        <label htmlFor="cleanup-confirm-phrase">Type <code>{expected}</code> to continue</label>
        <input
          id="cleanup-confirm-phrase"
          autoComplete="off"
          value={phrase}
          disabled={busy}
          onChange={event => setPhrase(event.target.value)}
        />
        <div className="say-dialog-actions">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="danger" disabled={!matches} loading={busy} loadingLabel="Starting cleanup…" onClick={() => onConfirm(phrase)}>
            Start cleanup
          </Button>
        </div>
      </section>
    </div>
  );
}


function JobDetails({ job, onRetry, retrying }) {
  if (!job) return null;
  return (
    <Surface className="cleanup-job-detail">
      <div className="cleanup-section-heading">
        <div>
          <span className="say-eyebrow">Cleanup job</span>
          <h2>{titleCase(job.status)}</h2>
          <p>Started {formatDate(job.startedAt || job.dateCreated)} · Sayable job {job.id}</p>
        </div>
        <div className="cleanup-job-actions">
          <StatusBadge status={statusTone(job.status)}>{titleCase(job.status)}</StatusBadge>
          {['partial', 'failed'].includes(job.status) ? (
            <Button size="sm" variant="secondary" loading={retrying} loadingLabel="Retrying…" onClick={() => onRetry(job.id)}>Retry failures</Button>
          ) : null}
        </div>
      </div>
      {job.status === 'processing' || job.status === 'queued' ? (
        <div className="cleanup-progress" role="status">
          <span className="cleanup-progress-spinner" aria-hidden="true" />
          <div><strong>Cleanup is running</strong><span>Provider and Sayable statuses will update here automatically.</span></div>
        </div>
      ) : null}
      <div className="cleanup-table-wrap">
        <table className="cleanup-table cleanup-result-table">
          <thead><tr><th>Organization</th><th>Sayable</th><th>Twilio</th><th>Stripe</th><th>Result</th></tr></thead>
          <tbody>{(job.items || []).map(item => (
            <tr key={item.id}>
              <td><strong>{item.name}</strong><small>{item.organizationId}</small></td>
              <td><StatusBadge status={statusTone(item.applicationStatus)} dot={false}>{titleCase(item.applicationStatus)}</StatusBadge></td>
              <td><StatusBadge status={statusTone(item.twilioStatus)} dot={false}>{titleCase(item.twilioStatus)}</StatusBadge>{item.cleanupDetails?.twilio?.numbersFound != null ? <small>{item.cleanupDetails.twilio.numbersReleased} of {item.cleanupDetails.twilio.numbersFound} numbers released</small> : null}</td>
              <td><StatusBadge status={statusTone(item.stripeStatus)} dot={false}>{titleCase(item.stripeStatus)}</StatusBadge></td>
              <td><StatusBadge status={statusTone(item.status)}>{titleCase(item.status)}</StatusBadge>{item.errorCodes?.length ? <small>{item.errorCodes.map(titleCase).join(', ')}</small> : null}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </Surface>
  );
}


function ProtectionControl({ organization, busy, onToggle }) {
  const protectionReasons = organization.protectionReasons || [];
  let actionLabel = 'Never delete';
  if (organization.protected) actionLabel = 'De-protect';
  if (organization.deprotectionOverride) actionLabel = 'Protect again';
  return (
    <div className="cleanup-protection">
      {organization.protected ? (
        <>
          <StatusBadge status="warning">Protected</StatusBadge>
          <small>{protectionReasons.join(' · ')}</small>
        </>
      ) : organization.deprotectionOverride ? (
        <>
          <StatusBadge status="success">De-protected</StatusBadge>
          <small>Every cleanup protection is overridden for this organization.</small>
        </>
      ) : null}
      <Button
        size="sm"
        variant={organization.protected ? 'quiet' : 'secondary'}
        loading={busy}
        onClick={() => onToggle(organization)}
      >
        {actionLabel}
      </Button>
    </div>
  );
}


function AccountCleanup({ checkLogin }) {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scope, setScope] = useState('inactive');
  const [protectedEmailText, setProtectedEmailText] = useState('');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [starting, setStarting] = useState(false);
  const [activeJob, setActiveJob] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [protectingId, setProtectingId] = useState('');

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setOverview(await getAccountCleanupOverview());
    } catch (requestError) {
      if (requestError?.response?.status === 403) {
        navigate('/overview', { replace: true });
        return;
      }
      setError(errorMessage(requestError, 'Account cleanup data could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const session = checkLogin?.();
    if (!session?.is_admin) {
      navigate('/overview', { replace: true });
      return;
    }
    loadOverview();
  }, [checkLogin, loadOverview, navigate]);

  useEffect(() => {
    if (!activeJob?.id || TERMINAL_JOB_STATUSES.has(activeJob.status)) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const job = await getAccountCleanupJob(activeJob.id);
        if (!cancelled) {
          setActiveJob(job);
          if (TERMINAL_JOB_STATUSES.has(job.status)) loadOverview();
        }
      } catch (requestError) {
        if (!cancelled) setError(errorMessage(requestError, 'Cleanup progress could not be refreshed.'));
      }
    };
    const intervalId = window.setInterval(poll, 2000);
    poll();
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [activeJob?.id, activeJob?.status, loadOverview]);

  const protectedEmails = useMemo(() => protectedEmailText
    .split(/[\n,;]+/)
    .map(value => value.trim().toLowerCase())
    .filter((value, index, values) => value && values.indexOf(value) === index), [protectedEmailText]);

  const organizationsWithPreviewProtections = useMemo(() => (
    (overview?.organizations || []).map((organization) => {
      if (organization.deprotectionOverride) return organization;
      const matchingEmails = (organization.memberEmails || [])
        .filter(email => protectedEmails.includes(String(email || '').toLowerCase()));
      if (!matchingEmails.length) return organization;
      return {
        ...organization,
        protected: true,
        protectionReasons: [
          ...(organization.protectionReasons || []),
          `Protected email: ${matchingEmails.join(', ')}`,
        ],
      };
    })
  ), [overview?.organizations, protectedEmails]);

  const visibleOrganizations = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return organizationsWithPreviewProtections;
    return organizationsWithPreviewProtections.filter(organization => [
      organization.name,
      organization.organizationId,
      ...(organization.memberEmails || []),
    ].some(value => String(value || '').toLowerCase().includes(query)));
  }, [organizationsWithPreviewProtections, search]);

  const toggleSelected = (organizationId) => {
    setSelectedIds(current => current.includes(organizationId)
      ? current.filter(id => id !== organizationId)
      : [...current, organizationId]);
  };

  const buildPreview = async () => {
    setPreviewing(true);
    setError('');
    setPreview(null);
    setActiveJob(null);
    try {
      const nextPreview = await createAccountCleanupPreview({
        environment: overview.environment,
        scope,
        protectedEmails,
        organizationIds: scope === 'selected' ? selectedIds : [],
      });
      setPreview(nextPreview);
    } catch (requestError) {
      setError(errorMessage(requestError, 'The cleanup preview could not be created.'));
    } finally {
      setPreviewing(false);
    }
  };

  const beginCleanup = async (confirmation) => {
    setStarting(true);
    setError('');
    try {
      const job = await startAccountCleanup({
        previewId: preview.id,
        environment: preview.environment,
        confirmation,
      });
      setActiveJob(job);
      setShowConfirmation(false);
      setPreview(null);
    } catch (requestError) {
      setError(errorMessage(requestError, 'The cleanup could not be started.'));
    } finally {
      setStarting(false);
    }
  };

  const toggleProtection = async (organization) => {
    setProtectingId(organization.organizationId);
    setError('');
    try {
      if (organization.protected) {
        await unprotectAccountCleanupOrganization(organization.organizationId);
      } else {
        await protectAccountCleanupOrganization(organization.organizationId, 'Protected in the account cleanup console');
      }
      await loadOverview();
    } catch (requestError) {
      setError(errorMessage(requestError, 'Organization protection could not be updated.'));
    } finally {
      setProtectingId('');
    }
  };

  const openJob = async (jobId) => {
    setError('');
    try {
      setActiveJob(await getAccountCleanupJob(jobId));
      setPreview(null);
    } catch (requestError) {
      setError(errorMessage(requestError, 'Cleanup job details could not be loaded.'));
    }
  };

  const retryJob = async (jobId) => {
    setRetrying(true);
    setError('');
    try {
      setActiveJob(await retryAccountCleanupJob(jobId));
    } catch (requestError) {
      setError(errorMessage(requestError, 'The failed cleanup items could not be retried.'));
    } finally {
      setRetrying(false);
    }
  };

  if (loading && !overview) {
    return <div className="page-content cleanup-page"><Skeleton width="46%" height={42} /><Skeleton height={140} /><Skeleton height={480} /></div>;
  }

  return (
    <div className="page-content cleanup-page">
      <PageHeader
        eyebrow="Platform administration"
        title="Account cleanup"
        description="Review and remove unused Sayable-linked Twilio and Stripe resources without crossing tenant or environment boundaries."
        actions={<><StatusBadge status={overview?.environment === 'prod' ? 'danger' : 'info'}>{String(overview?.environment || '').toUpperCase()}</StatusBadge><StatusBadge status="neutral">Platform admin only</StatusBadge></>}
      />

      {error ? <Alert tone="danger" title="Account cleanup needs attention">{error}</Alert> : null}
      <Alert tone="warning" title={`You are managing ${String(overview?.environment || '').toUpperCase()}`}>
        Previewing never changes an account. Starting a cleanup releases Twilio numbers, closes subaccounts, deletes Stripe customers, removes stored tenant files, and permanently deletes the organization’s Sayable database records. Only a minimal cleanup audit remains.
      </Alert>

      <div className="cleanup-summary-grid">
        {[
          ['Organizations', overview?.summary?.organizations || 0],
          ['Inactive', overview?.summary?.inactive || 0],
          ['Stripe status unavailable', overview?.summary?.subscriptionStatusUnavailable || 0],
          ['Twilio linked', overview?.summary?.twilioLinked || 0],
          ['Stripe linked', overview?.summary?.stripeLinked || 0],
          ['Protected', overview?.summary?.protected || 0],
        ].map(([label, value]) => <Surface key={label} className="cleanup-summary-card" padding="compact"><strong>{value}</strong><span>{label}</span></Surface>)}
      </div>

      <Surface className="cleanup-builder">
        <div className="cleanup-section-heading">
          <div><span className="say-eyebrow">1 · Select safely</span><h2>Choose what to clean up</h2><p>Inactive organizations are the safest default. You can review every exact organization before anything runs.</p></div>
        </div>
        <div className="cleanup-controls-grid">
          <fieldset className="cleanup-scope-fieldset">
            <legend>Organizations to include</legend>
            {[
              ['inactive', 'Inactive only', 'Organizations already disabled in Sayable.'],
              ['selected', 'Selected organizations', 'Only the rows you check below.'],
              ['all', 'All unprotected', 'Every organization not covered by a protection.'],
            ].map(([value, label, description]) => (
              <label key={value} className={`cleanup-scope-option${scope === value ? ' is-selected' : ''}`}>
                <input type="radio" name="cleanup-scope" value={value} checked={scope === value} onChange={() => setScope(value)} />
                <span><strong>{label}</strong><small>{description}</small></span>
              </label>
            ))}
          </fieldset>
          <div className="cleanup-protected-emails">
            <label htmlFor="cleanup-protected-emails"><strong>Protect by email</strong><span>Any matching team member protects their entire organization.</span></label>
            <textarea id="cleanup-protected-emails" rows="6" value={protectedEmailText} onChange={event => setProtectedEmailText(event.target.value)} placeholder={'owner@example.com\nimportant@example.com'} />
            <small>{protectedEmails.length ? `${protectedEmails.length} email${protectedEmails.length === 1 ? '' : 's'} protected in this preview` : 'Enter one email per line, or separate them with commas.'}</small>
          </div>
        </div>

        <div className="cleanup-inventory-heading">
          <div><strong>Organization inventory</strong><span>{scope === 'selected' ? `${selectedIds.length} selected` : 'Use search to review accounts and provider links.'}</span></div>
          <input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Search name, email, or Sayable ID" aria-label="Search organizations" />
        </div>
        <div className="cleanup-table-wrap">
          <table className="cleanup-table">
            <thead><tr><th className="cleanup-select-column">Include</th><th>Organization</th><th>Status</th><th>Provider resources</th><th>Protection</th></tr></thead>
            <tbody>{visibleOrganizations.map(organization => (
              <tr key={organization.organizationId} className={organization.protected ? 'is-protected' : ''}>
                <td className="cleanup-select-column"><input type="checkbox" checked={selectedIds.includes(organization.organizationId)} disabled={organization.protected} onChange={() => toggleSelected(organization.organizationId)} aria-label={`Select ${organization.name}`} /></td>
                <td><strong>{organization.name}</strong><small>{organization.memberEmails?.slice(0, 2).join(', ') || 'No member email'}{organization.memberEmails?.length > 2 ? ` +${organization.memberEmails.length - 2}` : ''}</small><code>{organization.organizationId}</code></td>
                <td>
                  <StatusBadge status={organization.subscriptionStatusAvailable === false ? 'warning' : organization.active ? 'success' : 'neutral'}>
                    {organization.subscriptionStatusAvailable === false ? 'Unknown' : organization.active ? 'Active' : 'Inactive'}
                  </StatusBadge>
                  <small>{subscriptionLabel(organization)} · Updated {formatDate(organization.dateUpdated)}</small>
                  {organization.billingLifecycle && <small>Billing: {titleCase(organization.billingLifecycle.state)}{organization.billingLifecycle.delete_after ? ` · Removal after ${new Date(organization.billingLifecycle.delete_after * 1000).toLocaleString()}` : ''}{organization.billingLifecycle.last_error ? ` · Review: ${organization.billingLifecycle.last_error}` : ''}</small>}
                  {organization.billingNoticesNeedReview > 0 && <small>{organization.billingNoticesNeedReview} billing email deliveries need review</small>}
                </td>
                <td><div className="cleanup-resource-list"><span className={organization.hasTwilioAccount ? 'is-linked' : ''}>Twilio {organization.twilioAccount || 'not linked'}</span><span className={organization.hasStripeCustomer ? 'is-linked' : ''}>Stripe {organization.stripeCustomer || 'not linked'}</span></div></td>
                <td><ProtectionControl organization={organization} busy={protectingId === organization.organizationId} onToggle={toggleProtection} /></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        {!visibleOrganizations.length ? <EmptyState title="No organizations match" description="Change your search or cleanup scope." /> : null}
        <div className="cleanup-builder-actions">
          <span>Preview is read-only and expires after 15 minutes.</span>
          <Button loading={previewing} loadingLabel="Building exact preview…" disabled={scope === 'selected' && !selectedIds.length} onClick={buildPreview}>Preview cleanup</Button>
        </div>
      </Surface>

      {preview ? (
        <Surface className="cleanup-preview">
          <div className="cleanup-section-heading">
            <div><span className="say-eyebrow">2 · Frozen preview</span><h2>{preview.totals?.organizations || 0} organizations selected</h2><p>This exact Sayable-ID selection is frozen until {formatDate(preview.expiresAt)}.</p></div>
            <Button variant="danger" disabled={!preview.items?.length} onClick={() => setShowConfirmation(true)}>Review and start</Button>
          </div>
          {preview.excluded?.length ? <Alert tone="success" title={`${preview.excluded.length} protected organization${preview.excluded.length === 1 ? '' : 's'} excluded`}>Protections are checked again immediately before each provider action.</Alert> : null}
          <div className="cleanup-preview-list">{(preview.items || []).map(item => (
            <div key={item.id} className="cleanup-preview-row"><div><strong>{item.name}</strong><small>{item.memberEmails?.join(', ') || 'No member email'} · {item.organizationId}</small></div><div><span>{item.hasTwilioAccount ? `Twilio ${item.twilioAccount}` : 'No Twilio account'}</span><span>{item.hasStripeCustomer ? `Stripe ${item.stripeCustomer}` : 'No Stripe customer'}</span></div></div>
          ))}</div>
          {!preview.items?.length ? <EmptyState title="Nothing will be deleted" description="Every matching organization is protected or the selected scope is empty." /> : null}
        </Surface>
      ) : null}

      <JobDetails job={activeJob} onRetry={retryJob} retrying={retrying} />

      <Surface className="cleanup-history">
        <div className="cleanup-section-heading"><div><span className="say-eyebrow">Audit trail</span><h2>Recent cleanup jobs</h2><p>Provider results and fallback-safe failures remain visible here.</p></div></div>
        {overview?.jobs?.length ? <div className="cleanup-history-list">{overview.jobs.map(job => (
          <button type="button" key={job.id} onClick={() => openJob(job.id)}>
            <span><strong>{titleCase(job.status)}</strong><small>{job.totals?.organizations || 0} organizations · {formatDate(job.dateCreated)}</small></span>
            <StatusBadge status={statusTone(job.status)}>{String(job.environment || '').toUpperCase()}</StatusBadge>
          </button>
        ))}</div> : <EmptyState title="No cleanup jobs yet" description="A completed preview and confirmed cleanup will appear here." />}
      </Surface>

      {showConfirmation ? <ConfirmationDialog preview={preview} busy={starting} onClose={() => setShowConfirmation(false)} onConfirm={beginCleanup} /> : null}
    </div>
  );
}


export default AccountCleanup;
